import { getCorePath } from '@multirx/shared/game/circuitEdit.js';
import type { Circuit } from '@multirx/shared/types/track.js';
import type { Car } from '@multirx/shared';
import {
  mountCircuit3dPreview,
  type Circuit3dPreviewHandle,
  type LivePlayer3D,
} from '../circuits/circuit3dPreview.js';
import { fetchCircuits, type SavedCircuit } from '../circuits/api.js';
import { fetchCars } from '../cars/api.js';
import {
  demoPositionAt,
  demoStepIndices,
  nextActivePlayer,
  pickDemoCircuit,
  pickDemoMoveSteps,
} from './presentationRace.js';

const PLAYER_COUNT = 4;
/** Durée d’anim d’une case → suivante (comme un pion en course). */
const CELL_MS = 210;
const CELL_PAUSE_MS = 35;
const BETWEEN_TURNS_MS = 320;
/** Change de circuit après N tours joués (présentation). */
const TURNS_PER_CIRCUIT = 10;
/** Démarre le préchargement du prochain circuit après N tours. */
const PRELOAD_AFTER_TURNS = 5;

/** Logo MultiRX coloré (nav + fallback accueil). */
export const MULTIRX_LOGO_SRC = '/assets/brand/multirx-logo.png?v=4';
/** SVG blanc — course 3D visible à travers le logo (accueil). */
export const MULTIRX_LOGO_MASK_SRC = '/assets/brand/multirx-logo.svg?v=4';
/** @deprecated alias — préférer MULTIRX_LOGO_SRC */
export const MULTIRX_WORDMARK_SRC = MULTIRX_LOGO_SRC;

export interface MultirxHeroHandle {
  dispose(): void;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Mélange Fisher–Yates — les voitures du hero sont bien aléatoires à chaque montage. */
export function shuffleCars(cars: readonly Car[]): Car[] {
  const copy = [...cars];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = copy[i];
    copy[i] = copy[j]!;
    copy[j] = tmp;
  }
  return copy;
}

/** Préfère les voitures avec modèle 3D, puis complète au hasard. */
export function pickRandomDemoCars(cars: readonly Car[], count: number): Car[] {
  const withModel = shuffleCars(cars.filter((c) => Boolean(c.model3dPath)));
  const without = shuffleCars(cars.filter((c) => !c.model3dPath));
  return [...withModel, ...without].slice(0, Math.max(0, count));
}

function applyWordmarkMask(el: HTMLElement): void {
  el.style.maskImage = `url("${MULTIRX_LOGO_MASK_SRC}")`;
  el.style.webkitMaskImage = `url("${MULTIRX_LOGO_MASK_SRC}")`;
}

interface StageSlot {
  el: HTMLDivElement;
  preview: Circuit3dPreviewHandle | null;
  circuitId: number | null;
  pathLen: number;
  pathIndices: number[];
  ready: Promise<void> | null;
}

/**
 * Hero MultiRX : logo en négatif, course 3D visible à travers
 * (présentation locale, rien n'est enregistré), caméra « voiture active ».
 */
export function mountMultirxHero(host: HTMLElement): MultirxHeroHandle {
  host.classList.add('multirx-hero');
  host.innerHTML = `
    <div class="multirx-hero-stage is-active" data-multirx-stage="a" aria-hidden="true"></div>
    <div class="multirx-hero-stage is-pending" data-multirx-stage="b" aria-hidden="true"></div>
    <img
      class="multirx-hero-fallback"
      src="${MULTIRX_LOGO_SRC}"
      alt=""
      width="1024"
      height="309"
      decoding="async"
      aria-hidden="true"
    />
    <h1 class="multirx-hero-sr">MultiRX</h1>
  `;

  const stageA = host.querySelector<HTMLDivElement>('[data-multirx-stage="a"]');
  const stageB = host.querySelector<HTMLDivElement>('[data-multirx-stage="b"]');
  if (!stageA || !stageB) {
    return { dispose() {} };
  }
  applyWordmarkMask(stageA);
  applyWordmarkMask(stageB);

  let disposed = false;
  let turnTimer: ReturnType<typeof setTimeout> | null = null;
  let activePlayer = 0;
  let paused = false;
  let turnRunning = false;
  let turnsOnCircuit = 0;
  let circuitPool: SavedCircuit[] = [];
  let carPool: Car[] = [];
  let preloadStarted = false;

  const slots: { active: StageSlot; pending: StageSlot } = {
    active: {
      el: stageA,
      preview: null,
      circuitId: null,
      pathLen: 0,
      pathIndices: [],
      ready: null,
    },
    pending: {
      el: stageB,
      preview: null,
      circuitId: null,
      pathLen: 0,
      pathIndices: [],
      ready: null,
    },
  };

  const stopSchedule = (): void => {
    if (turnTimer != null) {
      clearTimeout(turnTimer);
      turnTimer = null;
    }
  };

  const disposeSlot = (slot: StageSlot): void => {
    slot.preview?.dispose();
    slot.preview = null;
    slot.circuitId = null;
    slot.pathLen = 0;
    slot.pathIndices = [];
    slot.ready = null;
    slot.el.replaceChildren();
  };

  const tearAll = (): void => {
    stopSchedule();
    disposeSlot(slots.active);
    disposeSlot(slots.pending);
  };

  const scheduleTurn = (delayMs: number): void => {
    stopSchedule();
    if (disposed || paused) return;
    turnTimer = setTimeout(() => {
      void runTurn();
    }, delayMs);
  };

  const buildPlayers = (pathLen: number, pathIndices: number[]): LivePlayer3D[] => {
    const cars = pickRandomDemoCars(carPool, PLAYER_COUNT);
    return pathIndices.map((idx, i) => ({
      id: i,
      carName: cars[i]?.name ?? `Voiture ${i + 1}`,
      model3dPath: cars[i]?.model3dPath ?? null,
      position: demoPositionAt(idx),
      arrivalStamp: i + 1,
    }));
  };

  const mountIntoSlot = (slot: StageSlot, saved: SavedCircuit): boolean => {
    if (!saved.circuit || !slot.el.isConnected) return false;
    const circuit: Circuit = saved.circuit;
    const len = getCorePath(circuit.path).length || circuit.path.length;
    if (len < 4) return false;

    disposeSlot(slot);
    slot.pathLen = len;
    slot.circuitId = saved.id;
    slot.pathIndices = Array.from(
      { length: PLAYER_COUNT },
      (_, i) => Math.floor((i * len) / PLAYER_COUNT) % len
    );
    const players = buildPlayers(len, slot.pathIndices);
    const preview = mountCircuit3dPreview(slot.el, circuit, players, {
      interactive: true,
      initialCameraMode: 'active',
      cameraZoom: 1.5,
      getActivePlayerId: () => activePlayer,
      circuitName: saved.name,
    });
    preview.setCameraMode?.('active');
    slot.preview = preview;
    slot.ready = preview.ready;
    return true;
  };

  const promotePending = (): boolean => {
    const pending = slots.pending;
    if (!pending.preview || pending.pathLen < 4) return false;

    const oldActive = slots.active;
    slots.active = pending;
    slots.pending = {
      el: oldActive.el,
      preview: null,
      circuitId: null,
      pathLen: 0,
      pathIndices: [],
      ready: null,
    };

    oldActive.el.classList.remove('is-active');
    oldActive.el.classList.add('is-pending');
    slots.active.el.classList.remove('is-pending');
    slots.active.el.classList.add('is-active');

    // Dispose l’ancien après le swap visuel (prochain frame) pour éviter un trou.
    requestAnimationFrame(() => {
      oldActive.preview?.dispose();
      oldActive.preview = null;
      oldActive.el.replaceChildren();
    });

    activePlayer = 0;
    turnsOnCircuit = 0;
    preloadStarted = false;
    host.classList.add('is-live');
    host.classList.remove('is-empty');
    return true;
  };

  const startPreloadNext = (): void => {
    if (disposed || preloadStarted || circuitPool.length < 2) return;
    preloadStarted = true;
    const next = pickDemoCircuit(circuitPool, slots.active.circuitId);
    if (!next || !mountIntoSlot(slots.pending, next)) {
      preloadStarted = false;
    }
  };

  const rotateWhenReady = async (): Promise<boolean> => {
    if (circuitPool.length < 2) return false;
    if (!slots.pending.preview) {
      startPreloadNext();
    }
    if (!slots.pending.ready) return false;
    try {
      await slots.pending.ready;
    } catch {
      disposeSlot(slots.pending);
      preloadStarted = false;
      return false;
    }
    if (disposed) return false;
    return promotePending();
  };

  const runTurn = async (): Promise<void> => {
    const active = slots.active;
    if (disposed || paused || turnRunning || !active.preview?.movePlayerTo || active.pathLen <= 0) {
      return;
    }
    turnRunning = true;
    const player = activePlayer;
    const steps = pickDemoMoveSteps();
    const from = active.pathIndices[player] ?? 0;
    const cells = demoStepIndices(from, steps, active.pathLen);

    for (const idx of cells) {
      if (disposed || paused || !active.preview?.movePlayerTo) break;
      active.pathIndices[player] = idx;
      active.preview.movePlayerTo(player, demoPositionAt(idx), CELL_MS);
      await sleep(CELL_MS + CELL_PAUSE_MS);
    }

    if (!disposed) {
      activePlayer = nextActivePlayer(activePlayer, PLAYER_COUNT);
      turnsOnCircuit += 1;
    }
    turnRunning = false;

    if (disposed || paused) return;

    if (turnsOnCircuit >= PRELOAD_AFTER_TURNS) {
      startPreloadNext();
    }

    if (turnsOnCircuit >= TURNS_PER_CIRCUIT && circuitPool.length > 1) {
      if (await rotateWhenReady()) {
        scheduleTurn(500);
        return;
      }
      // Pas encore prêt : on continue sur le circuit courant et on réessaie plus tard.
    }
    scheduleTurn(BETWEEN_TURNS_MS);
  };

  const syncPause = (): void => {
    const onAccueil = document.getElementById('view-accueil')?.classList.contains('is-active');
    const visible = document.visibilityState === 'visible';
    const shouldRun = Boolean(onAccueil && visible && !disposed && slots.active.preview);
    if (shouldRun) {
      if (paused) {
        paused = false;
        if (!turnRunning) scheduleTurn(200);
      } else if (turnTimer == null && !turnRunning) {
        scheduleTurn(200);
      }
    } else {
      paused = true;
      stopSchedule();
    }
  };

  const onVis = (): void => syncPause();
  const onHash = (): void => syncPause();
  document.addEventListener('visibilitychange', onVis);
  window.addEventListener('hashchange', onHash);

  void (async () => {
    try {
      const [circuits, cars] = await Promise.all([
        fetchCircuits(50).catch(() => []),
        fetchCars(48).catch(() => [] as Car[]),
      ]);
      if (disposed || !stageA.isConnected) return;

      circuitPool = circuits;
      carPool = cars;
      const first = pickDemoCircuit(circuitPool, null);
      if (!first || !mountIntoSlot(slots.active, first)) {
        host.classList.add('is-empty');
        return;
      }

      // Attendre le premier chargement avant d’afficher (évite le flash bleu initial).
      await slots.active.ready;
      if (disposed) return;
      host.classList.add('is-live');
      host.classList.remove('is-empty');
      paused = false;
      scheduleTurn(600);
      syncPause();
    } catch {
      if (!disposed) host.classList.add('is-empty');
    }
  })();

  return {
    dispose(): void {
      disposed = true;
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('hashchange', onHash);
      tearAll();
      host.innerHTML = '';
    },
  };
}
